import React, { useMemo } from "react";
import { Card, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Inline, Stack } from "components/layout/reusableLayouts";
import SpendAreaChart from "components/common/SpendAreaChart";
import { TrendIndicator } from "@outpave/ds";
import type { TrendDirection } from "@outpave/ds";
import { formatCashFull } from "utils/formatCash";
import { SpendSummary } from "hooks/useByocDashboard";

export interface ExternalCardSpendChartProps {
  spend: SpendSummary;
  loading?: boolean;
  /** The spend query failed, so `spend` is the empty fallback rather than a
   *  measurement. Without this the chart reads a failure as zero spend. */
  error?: boolean;
}

/**
 * Company card spend across the trailing window.
 *
 * Domain wrapper only: the heading, the headline figure and the trend live
 * here, and the plot is `SpendAreaChart`. Same split as `PlatformBalanceChart`
 * over `SpendGraph`.
 *
 * The series is bucketed in the client because no server query returns daily
 * spend - `topSpending` is hardcoded to month buckets and `getCashFlow` returns
 * a weekly running balance, not spend.
 */
const ExternalCardSpendChart: React.FC<ExternalCardSpendChartProps> = ({
  spend,
  loading = false,
  error = false,
}) => {
  const { t } = useTranslation();

  const { changePercent } = spend.comparison;
  const hasComparison = !error && changePercent !== null;

  const direction: TrendDirection =
    !hasComparison || changePercent === 0
      ? "flat"
      : changePercent > 0
        ? "up"
        : "down";

  const points = useMemo(
    () => spend.daily.map((day) => ({ x: day.date, y: day.total })),
    [spend.daily],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        padding: "var(--space-5)",
        gap: "var(--space-6)",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* The header holds its size so the plot area below absorbs the row's
          fixed height rather than the heading and figure being squeezed. */}
      <Inline
        justify="between"
        align="start"
        gap="var(--space-4)"
        sx={{ flexShrink: 0 }}
      >
        <Stack gap="var(--space-3)" sx={{ flex: 1 }}>
          <Typography variant="h4" component="h3">
            {t("External card spend")}
          </Typography>
          <Typography variant="h2">
            {loading
              ? ""
              : error
                ? "-"
                : formatCashFull(spend.comparison.current, spend.currency)}
          </Typography>
          {!loading && hasComparison && (
            <Inline sx={{ flex: 1, justifyContent: "space-between" }}>
              <Typography
                variant="label"
                sx={{ color: "var(--color-text-tertiary)" }}
              >
                {t("Last {{n}} days", { n: spend.windowDays })}
              </Typography>
              <TrendIndicator
                direction={direction}
                sentiment="neutral"
                text={t("{{n}}% vs prior {{days}} days", {
                  n: Math.abs(changePercent),
                  days: spend.windowDays,
                })}
              />
            </Inline>
          )}
        </Stack>
      </Inline>

      <SpendAreaChart
        data={error ? [] : points}
        currency={spend.currency}
        loading={loading}
        emptyMessage={error ? t("Something went wrong") : undefined}
      />
    </Card>
  );
};

export default ExternalCardSpendChart;
